// biome-ignore-all lint/suspicious/noArrayIndexKey: fixed per-row cell arrays
// =============================================================================
// CustomerSyncUtilities — the two Unleashed Customer sync utilities
// =============================================================================
// Rendered on the Manage → DFV Services → Import Unleashed Data screen (ImportPage),
// under the Customer Inspector — the single home for all DFV Services / Unleashed tools
// (John, 3 Aug 2026; the "Sync with Unleashed" modal on the DFV Services list was
// retired). The standalone "Sync Utilities" screen was retired earlier (2 Aug 2026) —
// the Products / Warehouses / Customer Types / Taxes syncs moved to their home screens
// (Products / Dropdown Lists), leaving only these two Customer utilities.
//
// Customers are split into TWO utilities:
//   • Customer GUIDs   — link each DFV Service to its existing Unleashed Customer
//                        (records the GUID; no push). Run this first.
//   • Sync Customers   — a 2-WAY utility with a direction toggle: PUSH Portal →
//                        Unleashed (create/update via push-customers) or PULL
//                        Unleashed → Portal (write Unleashed values onto shelters).
// =============================================================================
import React from "react";
import { contactsFromCustomer } from "../../../supabase/functions/_shared/unleashed/contactMap";
import { listAddressesByShelterIds } from "../../data/deliveryAddresses";
import { listAllBankDetails } from "../../data/shelterBankDetails";
import { listAllShelters, updateShelterById } from "../../data/shelters";
import {
  listMembershipsForContactLink,
  updateMembershipContactById,
} from "../../data/userShelters";
import { planContactsLink } from "../../services/unleashedContactsLink";
import { planCustomerSync } from "../../services/unleashedCustomerSync";
import { logAudit } from "../../utils/auditLog";
import { getSupabaseClient } from "../../utils/supabase";
import { Btn } from "../../utils/ui.jsx";

const cellTh = {
  textAlign: "left",
  padding: "6px 10px",
  fontSize: 11,
  fontWeight: 700,
  color: "#607D79",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  borderBottom: "1px solid #E0EDEB",
};
const cellTd = { padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #F0F0F0" };
const cardStyle = {
  background: "white",
  borderRadius: 10,
  boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
  padding: 20,
  marginTop: 16,
};

// Map an Unleashed customer field to the shelters column a PULL writes it into.
const PULL_COL = {
  name: "shelter_name",
  customerType: "customer_type",
  salesGroup: "sales_group",
  taxable: "taxable",
  stopCredit: "stop_credit",
  abn: "abn",
  defaultWarehouse: "default_warehouse",
  // NB: bankName / bankAccount (separate table) and physicalAddress / postalAddress
  // (multi-column) have no PULL_COL — a PULL skips them (the pull loop ignores fields with
  // no mapping). They still PUSH; a pull-back of these can be added later if needed.
};

// Secondary (Cancel) button — clears a preview without applying anything.
function CancelBtn({ onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: "white",
        color: "#607D79",
        border: "1px solid #CFD8DC",
        borderRadius: 6,
        padding: "8px 16px",
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "default" : "pointer",
        fontFamily: "inherit",
      }}
    >
      Cancel
    </button>
  );
}

// One tick-box row. `checked` + `onToggle` drive the selection.
function Row({ checked, onToggle, cells, label }) {
  return (
    <tr>
      <td style={cellTd}>
        <input type="checkbox" checked={checked} onChange={onToggle} aria-label={label} />
      </td>
      {cells.map((c, i) => (
        <td key={i} style={cellTd}>
          {c}
        </td>
      ))}
    </tr>
  );
}

// A titled tick-box table for one plan bucket. When `onSelectAll` is provided a
// master checkbox sits to the LEFT of the first column that ticks/unticks every
// row in this table.
// Plain-English reasons a row couldn't be linked. Keyed by the planner's reason
// codes — the whole point is that "22 unmatched" becomes 22 actionable rows.
const UNMATCHED_LABELS = {
  no_customer: "No customer with this code in Unleashed",
  customer_has_no_contacts: "Customer found, but it has no contacts in Unleashed",
  no_contact_match: "No contact on this customer matched by email or name",
  match_without_guid: "Matched, but Unleashed returned no GUID",
  linked_code_missing: "Already linked here, but Unleashed no longer returns this code",
};

// Read-only counterpart to PlanTable: nothing here can be selected or applied,
// it's a report of what needs fixing (usually in Unleashed, not in the portal).
function ReportTable({ title, head, rows }) {
  if (rows.length === 0) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#C62828", marginBottom: 4 }}>
        {title}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {head.map((hh) => (
              <th key={hh} style={cellTh}>
                {hh}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              {r.cells.map((c, i) => (
                <td key={i} style={cellTd}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlanTable({ title, head, rows, selected, onToggle, onSelectAll }) {
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.key));
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#00695C", marginBottom: 4 }}>
        {title}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...cellTh, width: 28 }}>
              {onSelectAll ? (
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={() =>
                    onSelectAll(
                      rows.map((r) => r.key),
                      !allChecked
                    )
                  }
                  aria-label={`Select all — ${title}`}
                />
              ) : null}
            </th>
            {head.map((hh) => (
              <th key={hh} style={cellTh}>
                {hh}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Row
              key={r.key}
              checked={selected.has(r.key)}
              onToggle={() => onToggle(r.key)}
              cells={r.cells}
              label={r.label}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Both Customer utilities as one block — rendered on the Import Unleashed Data screen
// (ImportPage), under the Customer Inspector.
export function CustomerSyncUtilities({ user }) {
  return (
    <>
      <CustomerLinkSection user={user} />
      <CustomerSyncSection user={user} />
    </>
  );
}

// Utility 1 — Customer GUIDs. Fetches Unleashed Customers, plans against
// `shelters`, and surfaces ONLY the link bucket: match a service to its existing
// Unleashed Customer and record the GUID on shelters.unleashed_guid (a write to
// our own table — no Unleashed POST). Run this before the 2-way sync so the
// ~266 imported services can never be duplicated.
// Exported individually so ImportPage can place it as import "Step 4" (John, 4 Aug
// 2026 — the CSV-import flow is 5 sequential steps; the 2-way sync + inspector are
// separate ongoing utilities below).
export function CustomerLinkSection({ user }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [plan, setPlan] = React.useState(null);
  const [summary, setSummary] = React.useState("");
  const [selected, setSelected] = React.useState(() => new Set());

  const toggle = React.useCallback((key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const selectMany = React.useCallback((keys, check) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (check) next.add(k);
        else next.delete(k);
      }
      return next;
    });
  }, []);

  function cancel() {
    setPlan(null);
    setSelected(new Set());
    setSummary("");
    setError("");
  }

  async function runSync() {
    setBusy(true);
    setError("");
    setSummary("");
    setPlan(null);
    try {
      const supa = getSupabaseClient();
      if (!supa) {
        setError("Could not reach the server.");
        return;
      }
      const { data, error: invErr } = await supa.functions.invoke("unleashed-fetch", {
        body: { resource: "Customers" },
      });
      if (invErr) {
        setError(`Fetch failed: ${invErr.message}`);
        return;
      }
      if (!data || data.ok === false) {
        const st = data?.status ? ` (${data.status})` : "";
        setError(`Unleashed error: ${data?.error || "unknown"}${st}`);
        return;
      }
      const shelters = (await listAllShelters()) || [];
      const p = planCustomerSync(data.items || [], shelters);
      const keys = new Set();
      for (const l of p.toLink) keys.add(`link:${l.shelterId}`);
      setSelected(keys);
      setPlan(p);
    } catch (e) {
      setError(`Sync failed: ${e?.message || "error"}`);
    } finally {
      setBusy(false);
    }
  }

  // Record fetched GUIDs on our own shelters (no Unleashed POST). Client-side write.
  async function applyLinks() {
    if (!plan) return;
    setBusy(true);
    setError("");
    let linked = 0;
    try {
      for (const l of plan.toLink) {
        if (!selected.has(`link:${l.shelterId}`)) continue;
        const { error: e } = await updateShelterById(l.shelterId, { unleashed_guid: l.guid });
        if (e) throw new Error(e.message);
        linked += 1;
      }
      logAudit("unleashed_customers_linked", { linked, by: user?.shelterName });
      setSummary(`Linked ${linked} existing customer(s).`);
      setPlan(null);
    } catch (e) {
      setError(`Link failed: ${e?.message || "error"}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: "0 0 6px", fontSize: 16, color: "#00695C" }}>
        Step 4 — Link Customer GUIDs
      </h3>
      <p style={{ margin: "0 0 14px", fontSize: 13, color: "#607D79" }}>
        Match each DFV Service to its existing Unleashed Customer and record that Customer's GUID.
        Run this <strong>before</strong> the Sync Customers utility below — it's what stops the
        already-imported services being duplicated. Reads from Unleashed only; writes the GUID onto
        your own records.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <Btn onClick={runSync} disabled={busy}>
          {busy ? "Working…" : "Sync Customer GUIDs from Unleashed"}
        </Btn>
        {summary && (
          <span style={{ fontSize: 13, fontWeight: 600, color: "#2E7D32" }}>{summary}</span>
        )}
        {error && <span style={{ fontSize: 13, fontWeight: 600, color: "#C62828" }}>{error}</span>}
      </div>

      {plan && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 13, color: "#455A64", marginBottom: 12 }}>
            <strong>{plan.toLink.length}</strong> to link, <strong>{plan.inSyncCount}</strong>{" "}
            already linked / in sync
            {plan.unmatchedCount > 0 ? `, ${plan.unmatchedCount} unmatched` : ""}.
          </div>

          {plan.toLink.length > 0 ? (
            <>
              <PlanTable
                title="Link existing (record GUID — no push)"
                head={["Code", "Name", "GUID"]}
                rows={plan.toLink.map((l) => ({
                  key: `link:${l.shelterId}`,
                  label: `Link ${l.shelterCode}`,
                  cells: [l.shelterCode, l.shelterName, l.guid],
                }))}
                selected={selected}
                onToggle={toggle}
                onSelectAll={selectMany}
              />
              <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 14 }}>
                <Btn onClick={applyLinks} disabled={busy}>
                  {busy ? "Working…" : "Link existing Customers (backfill GUIDs)"}
                </Btn>
                <CancelBtn onClick={cancel} disabled={busy} />
              </div>
            </>
          ) : (
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "#607D79" }}>
                Everything is already linked — nothing to do.
              </span>
              <CancelBtn onClick={cancel} disabled={busy} />
            </div>
          )}

          {/* Anomalies — report only, nothing here can be applied. */}
          <ReportTable
            title={`Not linked (${plan.unmatched?.length || 0}) — needs attention`}
            head={["Code", "Name", "Reason"]}
            rows={(plan.unmatched || []).map((u) => ({
              key: `cunmatched:${u.shelterId}`,
              cells: [u.shelterCode, u.shelterName || "—", UNMATCHED_LABELS[u.reason] || u.reason],
            }))}
          />
        </div>
      )}
    </div>
  );
}

// Utility 2 — Sync Customers (2-WAY). A direction toggle chooses:
//   • PUSH Portal → Unleashed — create new customers + push changed fields, via
//     the push-customers Edge Function. Create is INTERLOCKED off while any
//     service still needs a GUID (run Customer GUIDs first).
//   • PULL Unleashed → Portal — write Unleashed's values onto our shelters (a
//     client-side write to our own table). Only "changed" rows can be pulled.
// A master tick-all/untick-all sits to the left of the Code column in each table.
// Exported individually so ImportPage can place it below the 5 import steps as an
// ongoing "utility" (John, 4 Aug 2026).
export function CustomerSyncSection({ user }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [plan, setPlan] = React.useState(null);
  const [summary, setSummary] = React.useState("");
  const [selected, setSelected] = React.useState(() => new Set());
  const [direction, setDirection] = React.useState("push"); // "push" | "pull"

  const toggle = React.useCallback((key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const selectMany = React.useCallback((keys, check) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (check) next.add(k);
        else next.delete(k);
      }
      return next;
    });
  }, []);

  function cancel() {
    setPlan(null);
    setSelected(new Set());
    setSummary("");
    setError("");
  }

  async function runSync() {
    setBusy(true);
    setError("");
    setSummary("");
    setPlan(null);
    try {
      const supa = getSupabaseClient();
      if (!supa) {
        setError("Could not reach the server.");
        return;
      }
      const { data, error: invErr } = await supa.functions.invoke("unleashed-fetch", {
        body: { resource: "Customers" },
      });
      if (invErr) {
        setError(`Fetch failed: ${invErr.message}`);
        return;
      }
      if (!data || data.ok === false) {
        const st = data?.status ? ` (${data.status})` : "";
        setError(`Unleashed error: ${data?.error || "unknown"}${st}`);
        return;
      }
      const shelters = (await listAllShelters()) || [];
      // Bank details live in a separate admin-only table — load them so the diff can
      // flag a bank-only change (keyed by shelter_id).
      const bankRows = await listAllBankDetails();
      const bankByShelterId = new Map(
        bankRows.map((b) => [
          b.shelter_id,
          { bank_name: b.bank_name, bank_account: b.bank_account },
        ])
      );
      // Delivery (Shipping) addresses — portal-owned SET; load the active ones (chunked
      // bulk read includes deactivated, so filter) so the diff can flag an address-only
      // change. delivery_addresses stores the lines as street/street2.
      const addrRows = (await listAddressesByShelterIds(shelters.map((s) => s.id))) || [];
      const deliveryByShelterId = new Map();
      for (const r of addrRows) {
        if (r.deactivated) continue;
        const arr = deliveryByShelterId.get(r.shelter_id) || [];
        arr.push({
          address_name: r.address_name ?? null,
          line1: r.street ?? null,
          line2: r.street2 ?? null,
          suburb_city: r.suburb_city ?? null,
          state: r.state ?? null,
          postcode: r.postcode ?? null,
          delivery_instruction: r.delivery_instruction ?? null,
          is_default: r.is_default ?? null,
        });
        deliveryByShelterId.set(r.shelter_id, arr);
      }
      const p = planCustomerSync(data.items || [], shelters, bankByShelterId, deliveryByShelterId);
      const keys = new Set();
      for (const c of p.toCreate) keys.add(`create:${c.shelterId}`);
      for (const u of p.toUpdate) if (u.defaultSelected) keys.add(`update:${u.shelterId}`);
      setSelected(keys);
      setPlan(p);
    } catch (e) {
      setError(`Sync failed: ${e?.message || "error"}`);
    } finally {
      setBusy(false);
    }
  }

  // PUSH — create/update the ticked rows in Unleashed via push-customers.
  async function applyPush() {
    if (!plan) return;
    setBusy(true);
    setError("");
    try {
      const supa = getSupabaseClient();
      if (!supa) {
        setError("Could not reach the server.");
        return;
      }
      const creates = plan.toCreate
        .filter((c) => selected.has(`create:${c.shelterId}`))
        .map((c) => c.shelterId);
      const updates = plan.toUpdate
        .filter((u) => selected.has(`update:${u.shelterId}`))
        .map((u) => u.shelterId);
      if (creates.length === 0 && updates.length === 0) {
        setSummary("Nothing ticked to push.");
        return;
      }
      const { data, error: invErr } = await supa.functions.invoke("push-customers", {
        body: { creates, updates },
      });
      if (invErr) {
        setError(`Push failed: ${invErr.message}`);
        return;
      }
      if (!data || data.ok === false) {
        setError(`Push error: ${data?.error || "unknown"}`);
        return;
      }
      const failNote = data.failed > 0 ? `, ${data.failed} failed` : "";
      setSummary(
        `Pushed — ${data.created} created, ${data.updated} updated${failNote}. Re-run the sync to refresh.`
      );
      // Surface WHY a row failed (the Unleashed rejection reason), not just the count.
      const firstFail = (data.results || []).find((r) => r && r.ok === false);
      if (firstFail) {
        setError(
          `Failure — ${firstFail.code || firstFail.shelterId}: ${firstFail.error || "unknown"}`
        );
      }
      setPlan(null);
    } catch (e) {
      setError(`Push failed: ${e?.message || "error"}`);
    } finally {
      setBusy(false);
    }
  }

  // PULL — write Unleashed's values (the change `from`) onto our shelters.
  async function applyPull() {
    if (!plan) return;
    setBusy(true);
    setError("");
    let pulled = 0;
    try {
      for (const u of plan.toUpdate) {
        if (!selected.has(`update:${u.shelterId}`)) continue;
        const patch = {};
        for (const ch of u.changes) {
          const col = PULL_COL[ch.field];
          if (!col) continue;
          // taxable + stopCredit are booleans stored as "true"/"false" strings in the diff.
          const isBool = ch.field === "taxable" || ch.field === "stopCredit";
          patch[col] = isBool ? (ch.from === null ? null : ch.from === "true") : ch.from;
        }
        if (Object.keys(patch).length === 0) continue;
        const { error: e } = await updateShelterById(u.shelterId, patch);
        if (e) throw new Error(e.message);
        pulled += 1;
      }
      logAudit("unleashed_customers_pulled", { pulled, by: user?.shelterName });
      setSummary(`Pulled ${pulled} customer(s) from Unleashed into the Portal.`);
      setPlan(null);
    } catch (e) {
      setError(`Pull failed: ${e?.message || "error"}`);
    } finally {
      setBusy(false);
    }
  }

  const isPush = direction === "push";

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: "0 0 6px", fontSize: 16, color: "#00695C" }}>Sync Customers (2-way)</h3>
      <p style={{ margin: "0 0 14px", fontSize: 13, color: "#607D79" }}>
        Reconcile DFV Services against Unleashed Customers in either direction.{" "}
        <strong>Push</strong> creates new customers and sends changed fields to Unleashed;{" "}
        <strong>Pull</strong> writes Unleashed's values back onto your services. Nothing happens
        until you review and apply.
      </p>

      {/* Direction toggle */}
      <div
        style={{
          display: "flex",
          border: "1px solid #CFD8DC",
          borderRadius: 8,
          overflow: "hidden",
          width: "fit-content",
          marginBottom: 14,
        }}
      >
        {[
          ["push", "Push: Portal → Unleashed"],
          ["pull", "Pull: Unleashed → Portal"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setDirection(id)}
            aria-pressed={direction === id}
            style={{
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              border: "none",
              cursor: "pointer",
              background: direction === id ? "#00695C" : "white",
              color: direction === id ? "white" : "#607D79",
              fontFamily: "inherit",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <Btn onClick={runSync} disabled={busy}>
          {busy ? "Working…" : "Sync Customers from Unleashed"}
        </Btn>
        {summary && (
          <span style={{ fontSize: 13, fontWeight: 600, color: "#2E7D32" }}>{summary}</span>
        )}
        {error && <span style={{ fontSize: 13, fontWeight: 600, color: "#C62828" }}>{error}</span>}
      </div>

      {plan && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 13, color: "#455A64", marginBottom: 12 }}>
            <strong>{plan.toCreate.length}</strong> new, <strong>{plan.toUpdate.length}</strong>{" "}
            changed, <strong>{plan.inSyncCount}</strong> in sync
            {plan.skippedDeactivated > 0 ? `, ${plan.skippedDeactivated} deactivated skipped` : ""}
            {plan.unmatchedCount > 0 ? `, ${plan.unmatchedCount} unmatched` : ""}.
          </div>

          {isPush ? (
            <>
              {plan.toCreate.length > 0 && (
                <PlanTable
                  title="New customers (create in Unleashed)"
                  head={["Code", "Name"]}
                  rows={plan.toCreate.map((c) => ({
                    key: `create:${c.shelterId}`,
                    label: `Create ${c.shelterCode}`,
                    cells: [c.shelterCode, c.shelterName],
                  }))}
                  selected={selected}
                  onToggle={toggle}
                  onSelectAll={selectMany}
                />
              )}

              {plan.toUpdate.length > 0 && (
                <PlanTable
                  title="Changed (push update to Unleashed)"
                  head={["Code", "Name", "Changes"]}
                  rows={plan.toUpdate.map((u) => ({
                    key: `update:${u.shelterId}`,
                    label: `Update ${u.shelterCode}`,
                    cells: [
                      u.shelterCode,
                      u.shelterName,
                      u.changes
                        .map((ch) => `${ch.field}: ${ch.from ?? "—"} → ${ch.to ?? "—"}`)
                        .join("; "),
                    ],
                  }))}
                  selected={selected}
                  onToggle={toggle}
                  onSelectAll={selectMany}
                />
              )}

              {plan.toCreate.length + plan.toUpdate.length > 0 ? (
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 14 }}>
                  <Btn onClick={applyPush} disabled={busy || plan.hasUnlinked}>
                    {busy ? "Working…" : "Push to Unleashed"}
                  </Btn>
                  <CancelBtn onClick={cancel} disabled={busy} />
                  {plan.hasUnlinked && (
                    <span style={{ fontSize: 12, color: "#C62828" }}>
                      Some services aren't linked yet — run Customer GUIDs first (prevents duplicate
                      creates).
                    </span>
                  )}
                </div>
              ) : (
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: "#607D79" }}>
                    Nothing to push — no new or changed customers.
                  </span>
                  <CancelBtn onClick={cancel} disabled={busy} />
                </div>
              )}
            </>
          ) : (
            <>
              {plan.toUpdate.length > 0 ? (
                <PlanTable
                  title="Changed (pull into Portal)"
                  head={["Code", "Name", "Portal → Unleashed"]}
                  rows={plan.toUpdate.map((u) => ({
                    key: `update:${u.shelterId}`,
                    label: `Pull ${u.shelterCode}`,
                    cells: [
                      u.shelterCode,
                      u.shelterName,
                      u.changes
                        .map((ch) => `${ch.field}: ${ch.to ?? "—"} → ${ch.from ?? "—"}`)
                        .join("; "),
                    ],
                  }))}
                  selected={selected}
                  onToggle={toggle}
                  onSelectAll={selectMany}
                />
              ) : null}

              {plan.toUpdate.length > 0 ? (
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 14 }}>
                  <Btn onClick={applyPull} disabled={busy}>
                    {busy ? "Working…" : "Pull to Portal"}
                  </Btn>
                  <CancelBtn onClick={cancel} disabled={busy} />
                </div>
              ) : (
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: "#607D79" }}>
                    Nothing to pull — no changed customers. (New customers can only be pushed.)
                  </span>
                  <CancelBtn onClick={cancel} disabled={busy} />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Utility 3 — Contact GUIDs. Matches each portal caseworker/contact (a user_shelters
// membership) to its existing Unleashed Customer Contact and records that Contact's
// GUID on user_shelters.unleashed_contact_guid — a write to our OWN table, no Unleashed
// POST. Run this (after Customer GUIDs) before enabling contact auto-sync so the
// already-imported contacts can never be duplicated. Reads Contacts straight off the
// Customers GET (Contacts[] returns on each customer), so it needs no per-contact call.
export function ContactLinkSection({ user }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [plan, setPlan] = React.useState(null);
  const [summary, setSummary] = React.useState("");
  const [selected, setSelected] = React.useState(() => new Set());

  const toggle = React.useCallback((key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const selectMany = React.useCallback((keys, check) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (check) next.add(k);
        else next.delete(k);
      }
      return next;
    });
  }, []);

  function cancel() {
    setPlan(null);
    setSelected(new Set());
    setSummary("");
    setError("");
  }

  async function runSync() {
    setBusy(true);
    setError("");
    setSummary("");
    setPlan(null);
    try {
      const supa = getSupabaseClient();
      if (!supa) {
        setError("Could not reach the server.");
        return;
      }
      // raw:true → the UNMAPPED customer rows, so each carries its Contacts[].
      const { data, error: invErr } = await supa.functions.invoke("unleashed-fetch", {
        body: { resource: "Customers", raw: true },
      });
      if (invErr) {
        setError(`Fetch failed: ${invErr.message}`);
        return;
      }
      if (!data || data.ok === false) {
        const st = data?.status ? ` (${data.status})` : "";
        setError(`Unleashed error: ${data?.error || "unknown"}${st}`);
        return;
      }
      const customers = (data.items || []).map((raw) => ({
        customerCode: raw.CustomerCode,
        contacts: contactsFromCustomer(raw),
      }));
      const memberships = (await listMembershipsForContactLink()) || [];
      const p = planContactsLink(memberships, customers);
      const keys = new Set();
      for (const l of p.toLink) keys.add(`clink:${l.membershipId}`);
      setSelected(keys);
      setPlan(p);
    } catch (e) {
      setError(`Sync failed: ${e?.message || "error"}`);
    } finally {
      setBusy(false);
    }
  }

  // Record fetched Contact GUIDs on our own memberships (no Unleashed POST).
  async function applyLinks() {
    if (!plan) return;
    setBusy(true);
    setError("");
    let linked = 0;
    try {
      for (const l of plan.toLink) {
        if (!selected.has(`clink:${l.membershipId}`)) continue;
        const { error: e } = await updateMembershipContactById(l.membershipId, {
          unleashed_contact_guid: l.contactGuid,
        });
        if (e) throw new Error(e.message);
        linked += 1;
      }
      logAudit("unleashed_contacts_linked", { linked, by: user?.shelterName });
      setSummary(`Linked ${linked} existing contact(s).`);
      setPlan(null);
    } catch (e) {
      setError(`Link failed: ${e?.message || "error"}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: "0 0 6px", fontSize: 16, color: "#00695C" }}>
        Step 5 — Link Contact GUIDs
      </h3>
      <p style={{ margin: "0 0 14px", fontSize: 13, color: "#607D79" }}>
        Match each caseworker/contact to its existing Unleashed Customer Contact and record that
        Contact's GUID. Run <strong>Step 4 — Link Customer GUIDs</strong> first (a contact needs its
        Customer linked). Reads from Unleashed only; writes the GUID onto your own records.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <Btn onClick={runSync} disabled={busy}>
          {busy ? "Working…" : "Sync Contact GUIDs from Unleashed"}
        </Btn>
        {summary && (
          <span style={{ fontSize: 13, fontWeight: 600, color: "#2E7D32" }}>{summary}</span>
        )}
        {error && <span style={{ fontSize: 13, fontWeight: 600, color: "#C62828" }}>{error}</span>}
      </div>

      {plan && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 13, color: "#455A64", marginBottom: 12 }}>
            <strong>{plan.toLink.length}</strong> to link,{" "}
            <strong>{plan.alreadyLinkedCount}</strong> already linked
            {plan.unmatchedCount > 0 ? `, ${plan.unmatchedCount} unmatched` : ""}.
          </div>

          {plan.toLink.length > 0 ? (
            <>
              <PlanTable
                title="Link existing contacts (record GUID — no push)"
                head={["Service", "Name", "Email", "Contact GUID"]}
                rows={plan.toLink.map((l) => ({
                  key: `clink:${l.membershipId}`,
                  label: `Link ${l.name || l.membershipId}`,
                  cells: [l.shelterCode, l.name || "—", l.email || "—", l.contactGuid],
                }))}
                selected={selected}
                onToggle={toggle}
                onSelectAll={selectMany}
              />
              <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 14 }}>
                <Btn onClick={applyLinks} disabled={busy}>
                  {busy ? "Working…" : "Link existing Contacts (backfill GUIDs)"}
                </Btn>
                <CancelBtn onClick={cancel} disabled={busy} />
              </div>
            </>
          ) : (
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "#607D79" }}>
                Everything is already linked — nothing to do.
              </span>
              <CancelBtn onClick={cancel} disabled={busy} />
            </div>
          )}

          {/* The contacts that couldn't be linked, with WHY. Report only — these are
              fixed in Unleashed (or on the Users tab), not from this screen. */}
          <ReportTable
            title={`Not linked (${plan.unmatched?.length || 0}) — needs attention`}
            head={["Service", "Name", "Email", "Reason"]}
            rows={(plan.unmatched || []).map((u) => ({
              key: `cunmatch:${u.membershipId}`,
              cells: [
                u.shelterCode,
                u.name || "—",
                u.email || "—",
                UNMATCHED_LABELS[u.reason] || u.reason,
              ],
            }))}
          />
        </div>
      )}
    </div>
  );
}
